import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAutenticacao } from '../context/autenticacao_contexto';
import { storage } from '../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { cores } from '../theme';

import {
    Typography, Container, Box, Paper, TextField, Button,
    Alert, CircularProgress, Avatar, LinearProgress, IconButton,
    Divider, Fade
} from '@mui/material';
import {
    PhotoCamera,
    Save as SaveIcon,
    ArrowBack,
    Person as PersonIcon
} from '@mui/icons-material';

const PerfilPagina = () => {
    const navegar = useNavigate();
    const { perfilUsuario, carregarPerfil, usuarioFirebase } = useAutenticacao();
    const [nome, setNome] = useState('');
    const [fotoUrl, setFotoUrl] = useState('');
    const [arquivo, setArquivo] = useState(null);
    const [progresso, setProgresso] = useState(0);
    const [processando, setProcessando] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');

    useEffect(() => {
        if (perfilUsuario) {
            setNome(perfilUsuario.nome_completo);
            setFotoUrl(perfilUsuario.foto_perfil_url);
        }
    }, [perfilUsuario]);

    const lidarComSelecaoDeArquivo = (e) => {
        const file = e.target.files[0];
        if (file) {
            setArquivo(file);
            setFotoUrl(URL.createObjectURL(file));
        }
    };

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setProcessando(true);
        setErro('');
        setSucesso('');

        let novaFotoUrl = perfilUsuario?.foto_perfil_url || '';

        try {
            if (arquivo) {
                try {
                    if (!usuarioFirebase || !usuarioFirebase.uid) {
                        console.warn("Usuário Firebase não disponível");
                        setErro("Não foi possível fazer o upload da foto. Atualizando apenas o nome.");
                    } else {
                        novaFotoUrl = await new Promise((resolve, reject) => {
                            const nomeArquivo = `foto_perfil/${usuarioFirebase.uid}/${Date.now()}-${arquivo.name}`;
                            const storageRef = ref(storage, nomeArquivo);
                            const uploadTask = uploadBytesResumable(storageRef, arquivo);

                            uploadTask.on('state_changed',
                                (snapshot) => setProgresso((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                                (error) => reject(error),
                                async () => {
                                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                    resolve(downloadURL);
                                }
                            );
                        });
                    }
                } catch (uploadError) {
                    console.error("Erro no upload:", uploadError);
                    setErro("Erro ao fazer upload da imagem. Atualizando apenas o nome.");
                    novaFotoUrl = perfilUsuario?.foto_perfil_url || '';
                }
            }

            const payload = {
                nome_completo: nome,
                foto_perfil_url: novaFotoUrl,
            };

            await api.put('/api/usuarios/meu-perfil', payload);
            setSucesso('Perfil atualizado com sucesso!');

            if (carregarPerfil) await carregarPerfil();

            setTimeout(() => navegar('/'), 2000);

        } catch (err) {
            setErro(err.response?.data?.erro || "Ocorreu um erro ao atualizar o perfil.");
        } finally {
            setProcessando(false);
        }
    };

    if (!perfilUsuario) return <CircularProgress />;

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Meu Perfil
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Edite suas informações pessoais
                            </Typography>
                        </Box>
                    </Box>

                    <Fade in={true} timeout={600}>
                        <Box component="form" onSubmit={lidarComSubmissao}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'center', md: 'flex-start' },
                                gap: 4,
                                mb: 4
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    mb: { xs: 2, md: 0 }
                                }}>
                                    <Avatar
                                        src={fotoUrl || undefined}
                                        sx={{
                                            width: 150,
                                            height: 150,
                                            mb: 2,
                                            border: `4px solid ${cores.fundo.papel}`,
                                            boxShadow: cores.sombras.suave
                                        }}
                                    >
                                        {!fotoUrl && <PersonIcon sx={{ fontSize: 80, color: cores.primaria.principal }} />}
                                    </Avatar>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        size="small"
                                        startIcon={<PhotoCamera />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: cores.primaria.principal,
                                            color: cores.primaria.principal,
                                            '&:hover': {
                                                borderColor: cores.primaria.escura,
                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.04)`
                                            }
                                        }}
                                    >
                                        Alterar Foto
                                        <input type="file" hidden accept="image/*" onChange={lidarComSelecaoDeArquivo} />
                                    </Button>
                                </Box>

                                <Box sx={{ flex: 1, width: '100%' }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            bgcolor: cores.fundo.claro,
                                            borderRadius: 2,
                                            border: `1px solid ${cores.cinza.claro}`,
                                            boxShadow: cores.sombras.suave
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mb: 2,
                                                color: cores.primaria.principal,
                                                fontWeight: 600
                                            }}
                                        >
                                            Informações Pessoais
                                        </Typography>

                                        <TextField
                                            label="Nome Completo"
                                            fullWidth
                                            required
                                            margin="normal"
                                            value={nome}
                                            onChange={(e) => setNome(e.target.value)}
                                            InputProps={{
                                                sx: { borderRadius: 2 }
                                            }}
                                        />

                                        <TextField
                                            label="E-mail"
                                            fullWidth
                                            margin="normal"
                                            value={perfilUsuario?.email || ''}
                                            disabled
                                            InputProps={{
                                                sx: { borderRadius: 2 }
                                            }}
                                        />

                                        <TextField
                                            label="Cargo"
                                            fullWidth
                                            margin="normal"
                                            value={perfilUsuario?.is_admin ? 'Administrador' :
                                                perfilUsuario?.is_teacher ? 'Professor' : 'Aluno'}
                                            disabled
                                            InputProps={{
                                                sx: { borderRadius: 2 }
                                            }}
                                        />
                                    </Paper>

                                    {processando && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="body2" sx={{ mb: 1, color: cores.primaria.principal }}>
                                                Enviando imagem ({Math.round(progresso)}%)
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progresso}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: cores.cinza.claro,
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: cores.primaria.principal
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {erro && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        backgroundColor: cores.erro.claro,
                                        borderColor: cores.erro.medio,
                                        color: cores.erro.escuro,
                                        '& .MuiAlert-icon': {
                                            alignItems: 'center'
                                        }
                                    }}
                                >
                                    {erro}
                                </Alert>
                            )}

                            {sucesso && (
                                <Alert
                                    severity="success"
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        backgroundColor: `rgba(${parseInt(cores.sucesso.principal.slice(1, 3), 16)}, ${parseInt(cores.sucesso.principal.slice(3, 5), 16)}, ${parseInt(cores.sucesso.principal.slice(5, 7), 16)}, 0.1)`,
                                        borderColor: cores.sucesso.principal,
                                        color: cores.sucesso.principal,
                                        '& .MuiAlert-icon': {
                                            alignItems: 'center'
                                        }
                                    }}
                                >
                                    {sucesso}
                                </Alert>
                            )}

                            <Box sx={{
                                mt: 4,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navegar('/')}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        borderColor: cores.cinza.medio,
                                        color: cores.cinza.escuro,
                                        '&:hover': {
                                            borderColor: cores.cinza.escuro,
                                            bgcolor: 'rgba(71, 85, 105, 0.04)'
                                        }
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={processando}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        bgcolor: cores.primaria.principal,
                                        fontWeight: 600,
                                        boxShadow: cores.sombras.suave,
                                        '&:hover': {
                                            bgcolor: cores.primaria.escuro,
                                        }
                                    }}
                                >
                                    {processando ? 'Salvando...' : 'Salvar Alterações'}
                                </Button>
                            </Box>
                        </Box>
                    </Fade>
                </Paper>
            </Fade>
        </Container>
    );
};

export default PerfilPagina;